/* eslint-disable */
import {
  GetTradingViewConfig,
  GetTradingViewHistory,
  GetTradingViewResolveSymbol,
  GetTradingViewSearchSymbol,
  GetTradingViewHistoryCombined,
} from 'services/apiServices';

// Global positions state to store current positions
let globalPositions: any = null;
let isUpdatingPositions = false; // Flag to prevent multiple updates

// Function to update global positions
export const updateGlobalPositions = (positions: any) => {
  if (isUpdatingPositions) {
    return;
  }

  isUpdatingPositions = true;
  globalPositions = positions;

  // Force reload chart immediately
  if (window.tvWidget && window.tvWidget.activeChart) {
    const chart = window.tvWidget.activeChart();
    if (chart?.resetData) {
      chart.resetData(); // This should trigger getBars
    }
  }

  // Reset flag after a short delay to prevent multiple calls
  setTimeout(() => {
    isUpdatingPositions = false;
  }, 1000);
};

// Function to get current positions
export const getCurrentPositions = () => {
  return globalPositions;
};

type Bar = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
};

export default {
  onReady: callback => {
    const defaultConfig = {
      exchanges: [],
      symbols_types: [],
      supported_resolutions: ['1', '5', '15', '30', '60', '1D', '1W', '1M'],
      supports_marks: false,
      supports_timescale_marks: false,
      supports_time: true,
      supports_search: true,
      supports_group_request: false,
    };
    GetTradingViewConfig()
      .then(data => {
        callback(data);
      })
      .catch(err => {
        console.log('Config request failed, using default config:', err);
        callback(defaultConfig);
      });
  },
  searchSymbols: (userInput, exchange, symbolType, onResultReadyCallback) => {
    GetTradingViewSearchSymbol({
      query: userInput,
      exchange: exchange,
      symbol_type: symbolType,
    })
      .then(data => {
        onResultReadyCallback(data);
      })
      .catch(err => {
        onResultReadyCallback([]);
        console.log(err);
      });
  },
  resolveSymbol: (
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback,
  ) => {
    GetTradingViewResolveSymbol({ symbol: symbolName })
      .then(data => {
        const symbolInfo = {
          name: data.name || symbolName,
          ticker: data.ticker || symbolName,
          description: data.description || data.name || symbolName,
          type: data.type || 'stock',
          session: data.session || '0930-1600',
          timezone: data.timezone || 'Etc/UTC',
          minmov: data.minmov || 1,
          pricescale: data.pricescale || 100,
          has_intraday:
            data.has_intraday !== undefined ? data.has_intraday : true,
          supported_resolutions: data.supported_resolutions || [
            '1',
            '5',
            '15',
            '30',
            '60',
            '1D',
          ],
          volume_precision: data.volume_precision || 2,
          data_status: data.data_status || 'streaming',
        };
        onSymbolResolvedCallback(symbolInfo);
      })
      .catch(err => {
        onResolveErrorCallback('Cannot resolve symbol.');
      });
  },
  getBars: async (
    symbolInfo,
    resolution,
    periodParams,
    onHistoryCallback,
    onErrorCallback,
  ) => {
    const { from, to, firstDataRequest } = periodParams;

    // Handle probe requests
    if (
      typeof from !== 'number' ||
      from <= 0 ||
      typeof to !== 'number' ||
      to <= 0
    ) {
      onHistoryCallback([], { noData: true });
      return;
    }

    try {
      // Check if we have positions available
      const currentPositions = getCurrentPositions();

      let data: { data: unknown; metadata: unknown };

      if (
        currentPositions &&
        Object.keys(currentPositions.contracts || {}).length > 0
      ) {
        // Check if chart symbol matches positions symbol
        const chartSymbol = symbolInfo.ticker || symbolInfo.name;
        const positionsSymbol = currentPositions.symbol;

        if (chartSymbol === positionsSymbol) {
          // Use TradingView History Combined API with positions
          const params = {
            from_ts: from,
            to_ts: to,
            resolution,
            positions: {
              symbol: currentPositions.symbol,
              contracts: currentPositions.contracts || {},
            },
          };

          data = await GetTradingViewHistoryCombined(params);
        } else {
          // Use regular TradingView History API without positions
          data = await GetTradingViewHistory({
            symbol: symbolInfo.ticker || symbolInfo.name,
            resolution,
            from_ts: from,
            to_ts: to,
            firstDataRequest,
          });
        }
      } else {
        // Use regular TradingView History API without positions
        data = await GetTradingViewHistory({
          symbol: symbolInfo.ticker || symbolInfo.name,
          resolution,
          from_ts: from,
          to_ts: to,
          firstDataRequest,
        });
      }

      let bars: Bar[] = [];
      if (data && Array.isArray(data.data)) {
        bars = data.data
          .map(bar => {
            let t = Number(bar.time);
            if (t < 9999999999) t = t * 1000; // convert seconds to ms
            return {
              time: t,
              open: Number(bar.open),
              high: Number(bar.high),
              low: Number(bar.low),
              close: Number(bar.close),
              volume: bar.volume !== undefined ? Number(bar.volume) : undefined,
            };
          })
          .filter(bar => bar.time >= from * 1000 && bar.time < to * 1000)
          .sort((a, b) => a.time - b.time)
          .filter(
            (bar, idx, arr) => idx === 0 || bar.time !== arr[idx - 1].time,
          ); // unique times
      }

      if (bars.length === 0) {
        onHistoryCallback([], {
          noData: (data?.metadata as { noData?: boolean })?.noData || false,
        });
      } else {
        onHistoryCallback(bars, {
          noData: (data?.metadata as { noData?: boolean })?.noData || false,
        });
      }
    } catch (err) {
      console.error('getBars error:', err);
      onErrorCallback('Failed to fetch bars.');
    }
  },
  subscribeBars: (
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscribeUID,
    onResetCacheNeededCallback,
  ) => {
    // Not implemented: add polling or websocket for real-time if needed
    console.log(
      '[subscribeBars]: Method call with subscribeUID:',
      subscribeUID,
    );
  },
  unsubscribeBars: subscriberUID => {
    // Not implemented
    console.log(
      '[unsubscribeBars]: Method call with subscriberUID:',
      subscriberUID,
    );
  },
};
